import { Controller } from "@hotwired/stimulus"

/**
 * Sortable Table Controller
 *
 * Adds client-side column sorting to any HTML table.
 * Sorts strings, numbers, dates, and currency values.
 *
 * Usage:
 *   <table data-controller="sortable-table">
 *     <thead>
 *       <tr>
 *         <th data-action="click->sortable-table#sort" data-sortable-table-key-param="name">
 *           Name <span class="sortable-header-icon">↕</span>
 *         </th>
 *         <th data-action="click->sortable-table#sort" data-sortable-table-key-param="amount" data-sortable-table-type-param="number">
 *           Amount <span class="sortable-header-icon">↕</span>
 *         </th>
 *         <th data-action="click->sortable-table#sort" data-sortable-table-key-param="date" data-sortable-table-type-param="date">
 *           Date <span class="sortable-header-icon">↕</span>
 *         </th>
 *       </tr>
 *     </thead>
 *     <tbody data-sortable-table-target="body">
 *       <tr>
 *         <td data-sort-value="alice">Alice</td>
 *         <td data-sort-value="1500">$15.00</td>
 *         <td data-sort-value="2026-01-15">Jan 15, 2026</td>
 *       </tr>
 *     </tbody>
 *   </table>
 */
export default class extends Controller {
  static targets = ["body"]

  connect() {
    this.currentKey = null
    this.currentDirection = "asc"
  }

  sort(event) {
    const th = event.currentTarget.closest("th")
    if (!th) return
    const key = event.params.key
    const type = event.params.type || "string"
    const colIndex = Array.from(th.parentElement.children).indexOf(th)

    // Toggle direction
    if (this.currentKey === key) {
      this.currentDirection = this.currentDirection === "asc" ? "desc" : "asc"
    } else {
      this.currentKey = key
      this.currentDirection = "asc"
    }

    // Update header styles
    th.closest("thead").querySelectorAll("th").forEach(h => {
      h.classList.remove("sortable-header--active", "sortable-header--asc", "sortable-header--desc")
    })
    th.classList.add("sortable-header--active", `sortable-header--${this.currentDirection}`)

    // Sort rows
    const tbody = this.hasBodyTarget ? this.bodyTarget : this.element.querySelector("tbody")
    const rows = Array.from(tbody.querySelectorAll("tr"))

    rows.sort((a, b) => {
      const aCell = a.children[colIndex]
      const bCell = b.children[colIndex]
      const aVal = aCell?.getAttribute("data-sort-value") || aCell?.textContent?.trim() || ""
      const bVal = bCell?.getAttribute("data-sort-value") || bCell?.textContent?.trim() || ""

      let result = 0

      switch (type) {
        case "number":
        case "currency":
          result = this.parseNumber(aVal) - this.parseNumber(bVal)
          break
        case "date":
          result = new Date(aVal) - new Date(bVal)
          break
        default:
          result = aVal.localeCompare(bVal, undefined, { sensitivity: "base" })
      }

      return this.currentDirection === "asc" ? result : -result
    })

    // Re-insert sorted rows
    rows.forEach(row => tbody.appendChild(row))
  }

  parseNumber(str) {
    // Remove $, commas, and other non-numeric chars except . and -
    const cleaned = str.replace(/[^0-9.\-]/g, "")
    return parseFloat(cleaned) || 0
  }
}
